import { useMemo } from 'react'
import { ProtectionResult, SafetyLevel, useTokenQuery } from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { COMMON_BASES } from 'uniswap/src/constants/routing'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { CurrencyInfo, TokenList } from 'uniswap/src/features/dataApi/types'
import { buildCurrency, buildCurrencyInfo, currencyIdToContractInput, gqlTokenToCurrencyInfo } from 'uniswap/src/features/dataApi/utils'
import { buildNativeCurrencyId, buildWrappedNativeCurrencyId, currencyIdToAddress, currencyIdToChain } from 'uniswap/src/utils/currencyId'
import { currencyId } from 'uniswap/src/utils/currencyId'
import { isSameAddress } from 'utilities/src/addresses'

// H2 链代币数据映射（从接口获取的正确地址）
const H2_TOKENS_MAP: Record<string, { symbol: string; name: string; decimals: number }> = {
  // ✅ 正确的 WH2 地址
  '0x1e69ed5584b562da61c69433b6bb5430f351b1de': { symbol: 'WH2', name: 'Wrapped H2', decimals: 18 },
  // ✅ 正确的 USDC 地址  
  '0x16eb04cc4be0ec712fc5b8d7b7de28fa6914ac0d': { symbol: 'USDC', name: 'USD Coin', decimals: 6 },
  // ✅ 正确的 USDT 地址
  '0xdd0d57e75e7b69d997ebf6be7a80c8b00166004e': { symbol: 'USDT', name: 'Tether USD', decimals: 6 },
}

export function useCurrencyInfo(
  _currencyId?: string,
  options?: { refetch?: boolean; skip?: boolean },
): Maybe<CurrencyInfo> {
  // 🚨 禁用Token查询：避免"Failed to fetch"错误
  const { data } = useTokenQuery({
    variables: currencyIdToContractInput(_currencyId ?? ''),
    skip: true, // 完全跳过Token查询
    fetchPolicy: options?.refetch ? 'cache-and-network' : 'cache-first',
  })

  return useMemo(() => {
    if (!_currencyId) {
      return undefined
    }

    // 如果有GraphQL数据，优先使用
    if (data?.token) {
      return gqlTokenToCurrencyInfo(data.token)
    }

    // 否则尝试从 COMMON_BASES获取或创建基本的CurrencyInfo对象
    try {
      const chainId = currencyIdToChain(_currencyId)
      const address = currencyIdToAddress(_currencyId)
      
      if (!chainId) {
        return undefined
      }

      // 首先检查是否是已知代币
      // 1. 检查 COMMON_BASES
      const commonBases = COMMON_BASES[chainId]
      if (commonBases) {
        const commonBase = commonBases.find((base) => 
          base.currency.isToken && isSameAddress(base.currency.address, address)
        )
        if (commonBase) {
          return commonBase
        }
      }

      // 2. 检查 H2 链的真实代币数据
      if (chainId === UniverseChainId.H2 && address) {
        const addressLower = address.toLowerCase()
        const h2TokenInfo = H2_TOKENS_MAP[addressLower]
        
        if (h2TokenInfo) {
          const currency = buildCurrency({
            chainId,
            address,
            decimals: h2TokenInfo.decimals,
            symbol: h2TokenInfo.symbol,
            name: h2TokenInfo.name,
          })
          
          if (currency) {
            return buildCurrencyInfo({
              currency,
              currencyId: _currencyId,
              logoUrl: undefined,
              safetyInfo: { tokenList: TokenList.Default, protectionResult: ProtectionResult.Benign },
              safetyLevel: SafetyLevel.Verified,
              isSpam: false,
            })
          }
        }
      }

      // 如果不是常见代币，构建基本的Currency对象
      const currency = buildCurrency({
        chainId,
        address,
        decimals: 18, // 默认精度
        symbol: 'UNKNOWN',
        name: 'Unknown Token',
      })

      if (!currency) {
        return undefined
      }

      // 构建CurrencyInfo对象
      return buildCurrencyInfo({
        currency,
        currencyId: _currencyId,
        logoUrl: undefined,
        safetyInfo: { tokenList: TokenList.Default, protectionResult: ProtectionResult.Benign },
        safetyLevel: SafetyLevel.StrongWarning,
        isSpam: false,
      })
    } catch (error) {
      return undefined
    }
  }, [data, _currencyId])
}

export function useNativeCurrencyInfo(chainId: UniverseChainId): Maybe<CurrencyInfo> {
  const nativeCurrencyId = buildNativeCurrencyId(chainId)
  return useCurrencyInfo(nativeCurrencyId)
}

export function useWrappedNativeCurrencyInfo(chainId: UniverseChainId): Maybe<CurrencyInfo> {
  const wrappedCurrencyId = buildWrappedNativeCurrencyId(chainId)
  return useCurrencyInfo(wrappedCurrencyId)
}
